import {useEffect, useState} from "react";
import {getSearchFilter, setSearchFilter} from "../modules/auth-persistor";
import HelperService from "../services/helper-service";
import client from "../modules/client";
import Loading from "./loading";
import {searchFilterSchema} from "../utils/schemas";
import {Dropdown, DropdownItem} from "react-bootstrap";
import Form from 'react-bootstrap/Form';
import MapPicker from "./map-picker";
import {AppEvents} from "../utils/constants";
import {useNotifyEventsService} from "../services/notify-events-service";

export default function LocationFilter( {onChange=null, ...props} ){

    const [filter, _setFilter] = useState(null);
    const [tmpRadius, setTmpRadius] = useState(null);
    const [tmpLocation, setTmpLocation] = useState({});
    const [loading, setLoading] = useState(false);
    const [showMapPicker, setShowMapPicker] = useState(false);

    const nes = useNotifyEventsService();

    function setFilter(newFilter, trigger=true){
        _setFilter(newFilter);
        setSearchFilter(newFilter);
        nes.trigger(AppEvents.searchFilterChanged, newFilter);
        if (trigger && 'function' === typeof onChange){
            onChange(newFilter);
        }
    }

    //init position as its needed for map init
    useEffect(() => {
        let f = getSearchFilter();
        if (!f){
            setLoading(true);
            getCurrentLocation()
                .then((res)=>{
                    reverseGeocode(res.lat, res.lon)
                        .then(loc=>{
                            f = searchFilterSchema.cast(loc);
                            setFilter(f);
                            initTmpValues(f);
                        })
                        .catch((err)=>{
                            throw err;
                        })
                })
                .catch((err)=>{
                    const defaultFilter = searchFilterSchema.cast();
                    setFilter(defaultFilter);
                    initTmpValues(defaultFilter);
                })
                .finally(()=>{
                    setLoading(false);
                })
        } else {
            setFilter(f, false);
            initTmpValues(f);
        }
        nes.on(AppEvents.searchFilterChanged, reloadInternalFilter);
    }, [])

    function reloadInternalFilter(){
        const f = getSearchFilter();
        _setFilter(f);
        initTmpValues(f);
    }

    function initTmpValues(f=null, keys=['radius', 'location']){
        if (!f){
            f = getSearchFilter();
        }
        if (!f){return false;}
        if (keys.includes('radius')){
            setTmpRadius(f.radius);
        }
        if (keys.includes('location')){
            setTmpLocation({
                lat: f.lat,
                lon: f.lon,
                address: f.address,
                city: f.city,
                area: f.area,
            });
        }
    }

    function pickLocation(location){
        const l = {};
        l.lat = location.lat ? location.lat : 40.00058;
        l.lon = location.lon ? location.lon : -75.296791;
        l.address = location.address ? location.address : null;
        l.city = location.city ? location.city : null;
        l.area = location.area ? location.area : null;
        setTmpLocation(l);
    }
    function cancelLocation(){
        initTmpValues(null, ['location']);
        setShowMapPicker(false);
    }
    function saveLocation(){
        const newFilter = Object.assign({}, filter, tmpLocation);
        setFilter(newFilter);
        setShowMapPicker(false);
    }

    function pickRadius(radius){
        filter.radius = radius;
        setTmpRadius(radius);
    }
    function cancelRadius(){
        initTmpValues(null, ['radius']);
    }
    function saveRadius(){
        filter.radius = tmpRadius;
        setFilter(filter);
    }

    async function getCurrentLocation(){
        return new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(
                    position => {
                        if (position?.coords?.latitude && position?.coords?.longitude) {
                            resolve({
                                lat: position.coords.latitude,
                                lon: position.coords.longitude,
                            });
                        } else {
                            resolve(null);
                        }
                    },
                    err => {
                        HelperService.log(err);
                        resolve({
                            lat: 40.00058,
                            lon: -75.296791,
                        }); // Resolve with default when there is an error
                    }
                );
            }
        );
    }


    async function reverseGeocode(lat, lon){
        return new Promise((resolve, reject) => {
            client(`https://api.maptiler.com/geocoding/${lon},${lat}.json?key=${process.env.REACT_APP_MAPTILER_API_KEY}&language=en`, {withCredentials:false})
            .then((res)=>{
                let newAddress = null;
                let filtered = null;
                let newCity = null;
                if (res?.data?.features?.length){
                    filtered = res.data.features.filter((f)=>{
                        return f.place_type[0]==='address';
                    });
                    newAddress = filtered && filtered.length ? filtered[0].place_name_en : null;

                    filtered = res.data.features.filter((f)=>{
                        return f.place_type[0]==='joint_submunicipality';
                    });
                    if (!filtered?.length){
                        filtered = res.data.features.filter((f)=>{
                            return f.place_type[0]==='joint_municipality';
                        });
                    }
                    if (!filtered?.length){
                        filtered = res.data.features.filter((f)=>{
                            return f.place_type[0]==='municipality';
                        });
                    }
                    if (!filtered?.length){
                        filtered = res.data.features.filter((f)=>{
                            return f.place_type[0]==='municipal_district';
                        });
                    }
                    newCity = filtered && filtered.length ? filtered[0].text_en : null;
                }
                return resolve({
                    city: newCity,
                    area: null,
                    address: newAddress,
                    lat: lat,
                    lon: lon,
                });
            })
            .catch((err)=>{
                return reject(err);
            })
        });
    }

    return (
        <>
            {loading===true && <Loading />}

            {!loading && <div className="d-flex gap-1 flex-wrap flex-lg-nowrap">

                {filter && <Dropdown show={showMapPicker}  drop='down-centered' className='dropdown-lg'>
                    <Dropdown.Toggle variant="light" size='sm' onClick={()=>{setShowMapPicker(!showMapPicker)}}>
                        <span className="mdi mdi-map-marker-outline"></span> {tmpLocation?.city}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="p-2">
                        <MapPicker lat={tmpLocation?.lat} lon={tmpLocation?.lon} onChange={(res)=>pickLocation(res)} />
                        <div className="mt-2 d-flex">
                            <DropdownItem as="div" className="p-0 me-1">
                                <a className="btn btn-primary w-100 mb-1" onClick={() => saveLocation()}>Save</a>
                            </DropdownItem>
                            <DropdownItem as="div" className="p-0">
                                <a className="btn btn-light w-100" onClick={()=>cancelLocation()}>Cancel</a>
                            </DropdownItem>
                        </div>
                    </Dropdown.Menu>
                </Dropdown>}

                {filter?.radius && tmpRadius && <Dropdown drop='down-centered' className='dropdown-md'>
                    <Dropdown.Toggle variant="light" size='sm'>
                        <span className="mdi mdi-radius-outline"></span> {tmpRadius} mi
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="p-2">
                        {/*<ExpoStepSlider*/}
                        {/*    intervals={[1, 2, 5, 10, 100, 1000]}*/}
                        {/*    min={2}*/}
                        {/*    max={4828}*/}
                        {/*    defaultValue={filter.radius}*/}
                        {/*    onChange={(e)=>pickRadius(e.target.value)}*/}
                        {/*/>*/}
                        <Form.Range step={2} min={2} max={3000} defaultValue={filter.radius} onChange={(e)=>pickRadius(e.target.value)} />
                        <div className="mt-2 d-flex">
                            <DropdownItem as="div" className="p-0 me-1">
                                <a className="btn btn-primary w-100 mb-1" onClick={() => saveRadius()}>Save</a>
                            </DropdownItem>
                            <DropdownItem as="div" className="p-0">
                                <a className="btn btn-light w-100" onClick={()=>cancelRadius()}>Cancel</a>
                            </DropdownItem>
                        </div>
                    </Dropdown.Menu>
                </Dropdown>}
            </div>}

        </>
    );
}
